import { useContext } from "react";

import { useTranslation } from "react-i18next";

import AuthContext from "context/auth/authContext";
import {
    getEnvironment,
    moveCarrierToBottom,
    sortAndReturnArray
} from "shared/functions";
import { usePermissions } from "shared/hooks/usePermissions";
import { FILE_TYPES } from "shared/types/enums";

import { SUPPORTED_EXTENSIONS } from "../constants";
import { FileType } from "../types";

const useFileTypes = () => {
    const { isAdminUser, isAdminManagerUser, isCarrierUser } =
        useContext(AuthContext);

    const { t } = useTranslation();
    const { isReadFileNotAllowed, isCreateFileNotAllowed } = usePermissions();

    const { isProductionEnv } = getEnvironment();

    const {
        BEX,
        BIN,
        CFG,
        CSV,
        ECFW,
        EXIM,
        FWC,
        HEX,
        PACK,
        PAR,
        PEM,
        SET,
        TAR,
        TPACK,
        ZIP
    } = SUPPORTED_EXTENSIONS;

    const types: FileType[] = [
        // 1-Wire authorization list
        {
            value: FILE_TYPES.OneWireAuthorizationList,
            extensions: [CSV],
            isAllowedToRender: true,
            hasInfo: true
        },

        // BLE firmware
        {
            value: FILE_TYPES.BleFw,
            extensions: [BIN],
            isAllowedToRender: true
        },

        // BlueNRG firmware
        {
            value: FILE_TYPES.BlueNrg,
            extensions: [BIN],
            isAllowedToRender: true
        },

        // Bluetooth authorization list
        {
            value: FILE_TYPES.BluetoothAuthorizationList,
            extensions: [CSV],
            isAllowedToRender: true,
            hasInfo: true
        },

        // Certificate
        {
            value: FILE_TYPES.Certificate,
            extensions: [PEM],
            isAllowedToRender: true
        },

        // Configuration
        {
            value: FILE_TYPES.Configuration,
            extensions: [CFG],
            isAllowedToRender: true
        },

        // DTB
        {
            value: FILE_TYPES.Dtb,
            extensions: [EXIM],
            isAllowedToRender: isAdminManagerUser
        },

        // Firmware
        {
            value: FILE_TYPES.Firmware,
            extensions: [EXIM, ECFW],
            isAllowedToRender: true
        },

        // Firmware bundle
        {
            value: FILE_TYPES.FirmwareBundle,
            extensions: [TPACK],
            isAllowedToRender: isAdminManagerUser
        },

        // FM CAN Chip firmware
        {
            value: FILE_TYPES.CanChipFw,
            extensions: [FWC],
            isAllowedToRender: isAdminUser
        },

        // FM OBD Chip firmware
        {
            value: FILE_TYPES.FmObdChipFw,
            extensions: [FWC],
            isAllowedToRender: isAdminManagerUser
        },

        // GNSS-M Download Assistant
        {
            value: FILE_TYPES.GnssmDownloadAssistant,
            extensions: [BIN],
            isAllowedToRender: isAdminUser
        },

        // GNSS-M firmware
        {
            value: FILE_TYPES.GnssmFw,
            extensions: [BIN],
            isAllowedToRender: isAdminUser
        },

        // Manual geofence scenario list
        ...(!isProductionEnv
            ? [
                  {
                      value: FILE_TYPES.ManualGeofenceList,
                      extensions: [CSV],
                      isAllowedToRender: true
                  }
              ]
            : []),

        // MeiG modem firmware
        {
            value: FILE_TYPES.MeiGModemFw,
            extensions: [PACK],
            isAllowedToRender: isAdminUser
        },

        // NETPRISMA modem firmware
        {
            value: FILE_TYPES.NetprismaModemFirmware,
            extensions: [BIN, PAR],
            isAllowedToRender: isAdminUser
        },

        // Quectel modem firmware
        {
            value: FILE_TYPES.QuectelModemFw,
            extensions: [BIN, PACK, ZIP],
            isAllowedToRender: isAdminUser
        },

        // Simcom Delta modem firmware
        {
            value: FILE_TYPES.SimcomModemFirmware,
            extensions: [BIN],
            isAllowedToRender: isAdminUser
        },

        // Carrier Advance firmware
        {
            value: FILE_TYPES.CarrierAdvanceFw,
            extensions: [HEX, BEX],
            isAllowedToRender: isAdminUser || isCarrierUser
        },

        // Carrier Advance settings
        {
            value: FILE_TYPES.CarrierAdvanceSettings,
            extensions: [SET],
            isAllowedToRender: isAdminUser || isCarrierUser
        },

        // Carrier APX firmware
        {
            value: FILE_TYPES.CarrierApxFw,
            extensions: [TAR],
            isAllowedToRender: isAdminUser || isCarrierUser
        },

        // Carrier APX settings
        {
            value: FILE_TYPES.CarrierApxSettings,
            extensions: [SET],
            isAllowedToRender: isAdminUser || isCarrierUser
        }
    ];

    const typesWithTranslations = types.map(type => {
        return {
            ...type,
            translatedName: t(`General##${type.value}`)
        };
    });

    const sortedTypes = sortAndReturnArray(
        typesWithTranslations,
        "translatedName"
    );

    moveCarrierToBottom(sortedTypes);

    const visibleTypes = sortedTypes.filter(type => type.isAllowedToRender);

    const isAllowedToUploadFile = (fileType: FILE_TYPES) => {
        const selectedFileType = types.find(type => type.value === fileType);

        return (
            !isReadFileNotAllowed &&
            !isCreateFileNotAllowed &&
            selectedFileType?.isAllowedToRender
        );
    };

    return { types, visibleTypes, isAllowedToUploadFile };
};

export { useFileTypes };
